import React, { useState, useEffect, useContext } from 'react'
import { Box, Button, CircularProgress, Typography } from '@mui/material'
import { AuthContext } from 'src/context/auth/authContext'
import { AlertContext } from 'src/context/alert/alertContext'
import { useParams } from 'react-router-dom'
import { notifyBookingResult, deleteBooking } from 'src/services/bookings.services'
import firebaseApp from 'firebase/compat/app'
// Pantalla pública (sin login) a la que llega el responsable desde el email para
// autorizar o rechazar una cita reservada por un colaborador role6.
// - Autorizar: marca authorized = true.
// - Rechazar: llama a deleteBookingCloud, que borra la cita y libera la franja en la
//   misma transacción. Esa callable permite la llamada SIN sesión solo si la cita sigue
//   con authorized === false, precisamente para dar servicio a esta pantalla.
const BookingAuthorize: React.FC = () => {
  const { idCita } = useParams<{ idCita: string }>()
  const { firebase } = useContext(AuthContext)
  const { showSnackbar } = useContext(AlertContext)
  // Arranca en true: si no, el primer render (sin datos y sin fetch aún lanzado) enseña
  // el mensaje de "no se encontraron datos" durante un instante en cada carga.
  const [loading, setLoading] = useState(true)
  // Guarda QUÉ acción está en curso, no solo si hay alguna: hace falta para poner el
  // spinner en el botón correcto.
  const [acting, setActing] = useState<null | 'authorize' | 'reject'>(null)
  const [bookingData, setBookingData] = useState<any | null>(null)
  const [resolved, setResolved] = useState<'authorized' | 'rejected' | null>(null)

  useEffect(() => {
    const fetchBooking = async () => {
      try {
        setLoading(true)
        const bookingDoc = await firebase.db.collection('bookings').doc(idCita).get()
        if (bookingDoc.exists) setBookingData(bookingDoc.data())
        else showSnackbar('error', 'No se encontró la cita.')
      } catch (error) {
        console.error('Error al cargar la cita:', error)
        showSnackbar('error', 'Error al cargar la cita.')
      } finally {
        setLoading(false)
      }
    }
    if (idCita) fetchBooking()
    // Solo debe recargar cuando cambia la cita. NO incluir showSnackbar: no está
    // memoizado, cambia de identidad en cada render y, tras rechazar (doc borrado),
    // dispararía fetchBooking en bucle mostrando "No se encontró la cita" sin parar.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idCita])

  const handleAuthorize = async () => {
    try {
      setActing('authorize')
      await firebase.db.collection('bookings').doc(idCita).update({ authorized: true, authorizedAt: Date.now() })
      // Avisar al colaborador de que su cita ha sido autorizada.
      await notifyBookingResult(bookingData, 'approved').catch((e) => console.error(e))
      setResolved('authorized')
      showSnackbar('success', 'Cita autorizada correctamente')
    } catch (error) {
      console.error('Error al autorizar la cita:', error)
      showSnackbar('error', 'Error al autorizar la cita')
    } finally {
      setActing(null)
    }
  }

  const handleReject = async () => {
    try {
      setActing('reject')
      // Avisar al colaborador del rechazo ANTES de borrar (necesitamos sus datos).
      await notifyBookingResult(bookingData, 'rejected').catch((e) => console.error(e))
      // Borramos el documento: deleteBooking llama a deleteBookingCloud, que borra la
      // cita y libera la franja en la misma transacción.
      await deleteBooking(idCita)

      // Ajustar el contador de citas de la compañía restándole uno
      if (bookingData?.companyId) {
        await firebase.db.collection('companies').doc(bookingData.companyId).update({
          bookingIdCount: firebaseApp.firestore.FieldValue.increment(-1)
        })
      }

      setResolved('rejected')
      showSnackbar('success', 'Cita rechazada')
    } catch (error) {
      console.error('Error al rechazar la cita:', error)
      showSnackbar('error', 'Error al rechazar la cita')
    } finally {
      setActing(null)
    }
  }

  if (loading && !bookingData) return <Typography style={{ marginTop: '3rem', textAlign: 'center' }}>Cargando datos de la cita...</Typography>
  if (!bookingData) return <Typography style={{ marginTop: '3rem', textAlign: 'center' }}>No se encontraron datos para esta cita.</Typography>

  const dateStr = `${bookingData.day}/${+bookingData.month + 1}/${bookingData.year} - ${bookingData.hourFormatted}`

  return (
    <Box display="flex" flexDirection="column" alignItems="center" gap={2} style={{ marginTop: '3rem', padding: '0 1rem' }}>
      <Typography variant="h6">Autorización de cita</Typography>

      <Typography><strong>Colaborador:</strong> {bookingData.patient?.name || 'No especificado'}</Typography>
      <Typography><strong>Campaña:</strong> {bookingData.campaign?.name || 'No especificado'}</Typography>
      <Typography><strong>Zona afectada:</strong> {bookingData.affectedZone?.name || 'No especificado'}</Typography>
      <Typography><strong>Fecha:</strong> {dateStr}</Typography>
      {bookingData.notes && <Typography><strong>Notas:</strong> {bookingData.notes}</Typography>}

      {resolved === 'authorized' || bookingData.authorized === true ? (
        <Typography variant="body2" style={{ marginTop: '1rem' }}>Esta cita ya ha sido autorizada ✅</Typography>
      ) : resolved === 'rejected' || bookingData.deleted === true ? (
        <Typography variant="body2" style={{ marginTop: '1rem' }}>Esta cita ha sido rechazada / cancelada ❌</Typography>
      ) : (
        <Box display="flex" flexDirection="column" alignItems="center" gap={1} style={{ marginTop: '1rem' }}>
          <Box display="flex" gap={2}>
            {/* El spinner va como startIcon y NO sustituye al texto: así el botón no
                cambia de ancho ni se queda sin etiqueta. color="inherit" es
                imprescindible — el primary de MUI es el mismo azul del botón contained
                y el spinner resultaba invisible. */}
            <Button
              variant="contained"
              color="primary"
              onClick={handleAuthorize}
              disabled={acting !== null}
              startIcon={acting === 'authorize' ? <CircularProgress size={16} color="inherit" /> : undefined}
              style={{ minWidth: 150 }}
            >
              Autorizar
            </Button>
            <Button
              variant="outlined"
              color="error"
              onClick={handleReject}
              disabled={acting !== null}
              startIcon={acting === 'reject' ? <CircularProgress size={16} color="inherit" /> : undefined}
              style={{ minWidth: 150 }}
            >
              Rechazar
            </Button>
          </Box>
          {/* Rechazar encadena email + borrado transaccional + contador: puede tardar
              varios segundos y sin este aviso la pantalla parece colgada. */}
          {acting !== null && (
            <Typography variant="caption" color="textSecondary">
              {acting === 'authorize' ? 'Autorizando la cita…' : 'Rechazando la cita…'} Esto puede tardar unos segundos.
            </Typography>
          )}
        </Box>
      )}
    </Box>
  )
}

export default BookingAuthorize
